import React from "react";
import { Button } from "react-bootstrap";

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage]);

  if (currentPage <= 4) {
    [2, 3, 4, 5].forEach((page) => pages.add(page));
  } else if (currentPage >= totalPages - 3) {
    [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1].forEach((page) => pages.add(page));
  } else {
    pages.add(currentPage - 1);
    pages.add(currentPage + 1);
  }

  const sortedPages = [...pages].sort((a, b) => a - b);
  const items = [];

  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push(`ellipsis-${page}`);
    }
    items.push(page);
  });

  return items;
};

const buttonStyle = {
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-sm)",
  minWidth: "34px",
};

const AdminPagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  itemLabel,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const firstItem = (currentPage - 1) * itemsPerPage + 1;
  const lastItem = Math.min(currentPage * itemsPerPage, totalItems);
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mt-4 pt-3 border-top">
      <div className="text-muted small" aria-live="polite">
        Showing <strong>{firstItem}</strong> to <strong>{lastItem}</strong> of{" "}
        <strong>{totalItems}</strong> {itemLabel}
      </div>

      <nav aria-label={`${itemLabel} pagination`}>
        <div className="d-flex flex-wrap align-items-center gap-1">
          <Button
            variant="light"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            style={buttonStyle}
          >
            Previous
          </Button>

          {visiblePages.map((item) => {
            if (typeof item === "string") {
              return (
                <span
                  key={item}
                  className="d-inline-flex align-items-center justify-content-center text-muted"
                  style={{ minWidth: "28px" }}
                  aria-hidden="true"
                >
                  &hellip;
                </span>
              );
            }

            const isActive = item === currentPage;
            return (
              <Button
                key={item}
                size="sm"
                aria-label={`Go to page ${item}`}
                aria-current={isActive ? "page" : undefined}
                onClick={() => onPageChange(item)}
                style={{
                  ...buttonStyle,
                  backgroundColor: isActive ? "var(--color-primary)" : "var(--color-surface)",
                  color: isActive ? "#fff" : "var(--color-text-secondary)",
                  borderColor: isActive ? "var(--color-primary)" : "var(--color-border)",
                  fontWeight: "500",
                }}
              >
                {item}
              </Button>
            );
          })}

          <Button
            variant="light"
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            style={buttonStyle}
          >
            Next
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default AdminPagination;
